import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { ptBR } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Picker de data + hora em formato 24h.
 *
 * Substitui o <input type="datetime-local"> nativo (cujo formato 12h/24h é
 * ditado pelo navegador/SO e não pela aplicação). value/onChange usam o mesmo
 * formato local "YYYY-MM-DDTHH:mm" do datetime-local, então o restante da
 * página (parsing/validação) continua funcionando sem alterações.
 */

const pad2 = (value) => String(value).padStart(2, "0");

const parseLocalValue = (value) => {
  if (!value || typeof value !== "string") return null;
  const [datePart, timePart = "00:00"] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const formatDisplay = (date) =>
  date
    ? `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`
    : "Selecionar";

const DateTimePicker24h = ({
  value,
  onChange,
  ariaLabel,
  className,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const date = parseLocalValue(value);
  const hours = date ? date.getHours() : 0;
  const minutes = date ? date.getMinutes() : 0;

  const emit = (nextDate) => onChange?.(formatLocalValue(nextDate));

  const handleDaySelect = (day) => {
    if (!day) return;
    const base = date || new Date();
    emit(
      new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        base.getHours(),
        base.getMinutes(),
        0,
        0
      )
    );
  };

  const handleTimeChange = (part, raw) => {
    if (raw === "") return;
    let num = Number(raw);
    if (!Number.isFinite(num)) return;
    const base = date || new Date();
    const next = new Date(base);
    if (part === "hours") {
      num = Math.min(23, Math.max(0, Math.trunc(num)));
      next.setHours(num);
    } else {
      num = Math.min(59, Math.max(0, Math.trunc(num)));
      next.setMinutes(num);
    }
    emit(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "h-9 w-[205px] justify-start font-normal",
            !date && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {formatDisplay(date)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date || undefined}
          defaultMonth={date || undefined}
          onSelect={handleDaySelect}
          locale={ptBR}
          initialFocus
        />
        <div className="flex items-center justify-center gap-2 border-t p-3">
          <span className="text-sm text-muted-foreground">Hora</span>
          <Input
            type="number"
            min={0}
            max={23}
            value={pad2(hours)}
            onChange={(event) => handleTimeChange("hours", event.target.value)}
            className="h-9 w-16 text-center"
            aria-label="Hora (0 a 23)"
          />
          <span className="text-sm font-medium">:</span>
          <Input
            type="number"
            min={0}
            max={59}
            value={pad2(minutes)}
            onChange={(event) => handleTimeChange("minutes", event.target.value)}
            className="h-9 w-16 text-center"
            aria-label="Minuto"
          />
          <span className="text-xs text-muted-foreground">24h</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateTimePicker24h;
